import React from "react";
import { Block, SummaryRow } from "../../blockModel/types";
import { RenderCtx, es, editableProps, contentProps, renderTextWithVars } from "../renderText";

/**
 * True when a stored value represents the ${mapLink} "Get Directions" button.
 * Matches the clean token, and also self-heals values that a previous build
 * corrupted by writing the rendered button HTML back into the field (so the
 * canvas shows a button instead of raw "<span style=...>" markup).
 */
export function isMapLinkValue(value?: string): boolean {
	const s = String(value ?? "");
	if (s.includes("${mapLink}")) return true;
	return s.includes("Get Directions") && s.includes("inline-block");
}

/**
 * Renders the value of a single cell — if the value represents ${mapLink} we
 * always show a non-clickable "Get Directions" button chip (edit + preview).
 * The chip cell is NEVER contentEditable, so the ${mapLink} token is preserved
 * in the block model and never overwritten by contentEditable write-back.
 */
function renderValueNode(text: string, ctx: RenderCtx, extraStyle?: React.CSSProperties): React.ReactNode {
	if (isMapLinkValue(text)) {
		// Render as a non-clickable "Get Directions" button chip — matches backend output.
		// Brand bg #005e9e, white text, padding 10px 18px, radius 8 per render-check.html.
		return (
			<span
				style={{
					display: "inline-block",
					background: "var(--brand, #005e9e)",
					color: "#fff",
					fontSize: 13,
					fontWeight: 700,
					padding: "10px 18px",
					borderRadius: 8,
					pointerEvents: "none",
					whiteSpace: "nowrap",
					...extraStyle,
				}}>
				Get Directions
			</span>
		);
	}
	// Otherwise delegate to the normal text-with-vars renderer
	return <>{renderTextWithVars(text, ctx)}</>;
}

export function renderSummary(block: Block, ctx: RenderCtx): JSX.Element {
	const id = block.id ?? "";
	const rows = (block.data.rows ?? []) as SummaryRow[];
	const editable = !!ctx.editable;
	const brand = ctx.theme.brand;

	return (
		<div
			className="e-body"
			style={{ paddingTop: 8, paddingBottom: 8 }}>
			<div className="e-summary-c2">
				{rows.map((row, i) => {
					// ── Section header raw row (# Title) ──────────────────────────
					if (row.raw != null) {
						const title = row.raw.replace(/^#\s*/, "");
						const rawKey = id + ":rraw:" + i;
						return (
							<div
								key={i}
								className="e-sh"
								data-row={id + ":" + i}
								style={{
									position: "relative",
									fontSize: 14.5,
									fontWeight: 700,
									color: "#1a2940",
									margin: "18px 0 4px",
									paddingLeft: 12,
								}}>
								<span
									style={{
										position: "absolute",
										left: 0,
										top: "50%",
										transform: "translateY(-50%)",
										width: 4,
										height: 16,
										borderRadius: 2,
										background: brand,
										display: "inline-block",
									}}
								/>
								<span
									{...(editable ? editableProps(rawKey, "rraw:" + i) : {})}
									{...contentProps(title, ctx)}></span>
							</div>
						);
					}

					// ── Pair row: label2 set → one flex line, value-axis shared ───
					if (row.label2 !== undefined) {
						const lblKey = id + ":rlbl:" + i;
						const valKey = id + ":rval:" + i;
						const lbl2Key = id + ":rlbl2:" + i;
						const val2Key = id + ":rval2:" + i;
						const appliedLblStyle = es(ctx.elStyle, lblKey);
						const appliedValStyle = es(ctx.elStyle, valKey);
						const appliedLbl2Style = es(ctx.elStyle, lbl2Key);
						const appliedVal2Style = es(ctx.elStyle, val2Key);
						const isEven = (i + 1) % 2 === 0;
						const isEmphasis = row.emphasis || row.emphasis2;
						const rowBg = isEmphasis ? "#eef6fc" : isEven ? "#f6f8fb" : "transparent";

						// When value2 is ${mapLink}, the right side renders as a "Get Directions"
						// button chip. The label2 slot is hidden (mirrors backend: address left +
						// button right, no "Map:" label shown). The editable spans for rlbl2/rval2
						// are still rendered (display:none / zero-size) so write-back works, but
						// they are invisible to the user. The address value (left) must wrap freely.
						const isMapLinkPair = isMapLinkValue(row.value2);

						return (
							<div
								key={i}
								className={"e-row-c2 e-row-pair-c2" + (isEmphasis ? " e-row-em-c2" : "")}
								data-row={id + ":" + i}
								style={{
									display: "flex",
									alignItems: "center",
									flexWrap: "nowrap",
									padding: "9px 12px",
									borderRadius: 7,
									background: rowBg,
									gap: 0,
								}}>
								{/* Left label — fixed 148px */}
								<span
									className="lbl"
									style={{
										flex: "0 0 148px",
										width: 148,
										fontSize: 13,
										color: isEmphasis ? "#1a2940" : "#6e7f93",
										fontWeight: isEmphasis ? 700 : 400,
										...appliedLblStyle,
									}}
									{...(editable ? editableProps(lblKey, "rlbl:" + i) : {})}
									{...contentProps(row.label ?? "", ctx)}
								/>
								{/* Left value — wrappable when address + button-pair; no-wrap otherwise */}
								<span
									className={"val" + (row.emphasis ? " emph" : "")}
									style={{
										flex: isMapLinkPair ? "1 1 auto" : "0 0 calc(55% - 148px)",
										minWidth: 0,
										fontSize: row.emphasis ? 17 : 14,
										fontWeight: row.emphasis ? 800 : 600,
										color: row.emphasis ? brand : "#1a2940",
										whiteSpace: isMapLinkPair ? "normal" : "nowrap",
										wordBreak: isMapLinkPair ? "break-word" : undefined,
										paddingRight: isMapLinkPair ? 12 : undefined,
										...appliedValStyle,
									}}
									{...(editable ? editableProps(valKey, "rval:" + i) : {})}
									{...contentProps(row.value ?? "", ctx)}
								/>
								{isMapLinkPair ? (
									/* Get Directions button chip — NON-editable so the ${mapLink}
									   token is preserved in the model (never overwritten by
									   contentEditable write-back). No label2 for this side. */
									<span
										className="val2"
										style={{
											flex: "0 0 auto",
											...appliedVal2Style,
										}}>
										{renderValueNode("${mapLink}", ctx)}
									</span>
								) : (
									<>
										{/* Right label */}
										<span
											className="lbl2"
											style={{
												flex: "0 0 auto",
												fontSize: 13,
												color: "#6e7f93",
												margin: "0 8px 0 12px",
												whiteSpace: "nowrap",
												...appliedLbl2Style,
											}}
											{...(editable ? editableProps(lbl2Key, "rlbl2:" + i) : {})}
											{...contentProps(row.label2 ?? "", ctx)}
										/>
										{/* Right value */}
										<span
											className={"val2" + (row.emphasis2 ? " emph" : "")}
											style={{
												flex: "0 0 auto",
												fontSize: row.emphasis2 ? 17 : 14,
												fontWeight: row.emphasis2 ? 800 : 600,
												color: row.emphasis2 ? brand : "#1a2940",
												whiteSpace: "nowrap",
												...appliedVal2Style,
											}}
											{...(editable ? editableProps(val2Key, "rval2:" + i) : {})}
											{...contentProps(row.value2 ?? "", ctx)}
										/>
									</>
								)}
							</div>
						);
					}

					// ── Single row: label-left 148px · value-right ─────────────────
					const lblKey = id + ":rlbl:" + i;
					const valKey = id + ":rval:" + i;
					const appliedLblStyle = es(ctx.elStyle, lblKey);
					const appliedValStyle = es(ctx.elStyle, valKey);
					const isEven = (i + 1) % 2 === 0;
					const isEmphasis = !!row.emphasis;
					const rowBg = isEmphasis ? "#eef6fc" : isEven ? "#f6f8fb" : "transparent";

					// map link in value — always render as Get Directions chip (edit + preview)
					const isMapLink = isMapLinkValue(row.value);

					return (
						<div
							key={i}
							className={"e-row-c2" + (isEmphasis ? " e-row-em-c2" : "")}
							data-row={id + ":" + i}
							style={{
								display: "flex",
								alignItems: "baseline",
								padding: "9px 12px",
								borderRadius: 7,
								background: rowBg,
							}}>
							<span
								className="lbl"
								style={{
									flex: "0 0 148px",
									width: 148,
									fontSize: 13,
									color: isEmphasis ? "#1a2940" : "#6e7f93",
									fontWeight: isEmphasis ? 700 : 400,
									...appliedLblStyle,
								}}
								{...(editable ? editableProps(lblKey, "rlbl:" + i) : {})}
								{...contentProps(row.label ?? "", ctx)}
							/>
							{isMapLink ? (
								<span style={{ flex: 1 }}>{renderValueNode("${mapLink}", ctx)}</span>
							) : (
								<span
									className={"val" + (isEmphasis ? " emph" : "")}
									style={{
										flex: 1,
										fontSize: isEmphasis ? 17 : 14,
										fontWeight: isEmphasis ? 800 : 600,
										color: isEmphasis ? brand : "#1a2940",
										...appliedValStyle,
									}}
									{...(editable ? editableProps(valKey, "rval:" + i) : {})}
									{...contentProps(row.value ?? "", ctx)}
								/>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}
